import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { LEADS } from "@/constants/testIds";
import { toast, Toaster } from "sonner";
import { X, Upload, Trash2, CheckSquare, Square, AlertTriangle, Plus, Search, UserCircle, Sparkles } from "lucide-react";
import LeadsImportModal from "@/components/LeadsImportModal";
import ManualAddLeadModal from "@/components/ManualAddLeadModal";

const COLUMNS = [
  { key: "new", label: "New" },
  { key: "contacted", label: "Contacted" },
  { key: "website_sent", label: "Website Sent" },
  { key: "interested", label: "Interested" },
  { key: "closed_won", label: "Closed Won" },
  { key: "closed_lost", label: "Closed Lost" },
];

const SINCE_MS = { "24h": 24 * 3600e3, "7d": 7 * 86400e3, "30d": 30 * 86400e3 };

export default function LeadsBoard() {
  const [leads, setLeads] = useState([]);
  const [dragId, setDragId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showImport, setShowImport] = useState(false);
  const [showManual, setShowManual] = useState(false);
  const [sourceQuery, setSourceQuery] = useState("");
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [confirmNuke, setConfirmNuke] = useState(false);
  const [nukeInput, setNukeInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [users, setUsers] = useState([]);
  const [purgePreview, setPurgePreview] = useState(null);  // { matched, preview[] }
  const [purging, setPurging] = useState(false);

  const statusFilter = searchParams.get("status") || "";
  const sinceFilter = searchParams.get("since") || "";
  const ownerFilter = searchParams.get("owner") || "";

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/leads");
      setLeads(data.leads);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    api.get("/users").then(({ data }) => setUsers(data.users || [])).catch(() => setUsers([]));
  }, [load]);

  const setOwnerFilter = (v) => {
    const next = new URLSearchParams(searchParams);
    if (v) next.set("owner", v); else next.delete("owner");
    setSearchParams(next);
  };

  const openPurgeTest = async () => {
    setPurging(true);
    try {
      const { data } = await api.post("/leads/purge-test", { dry_run: true });
      setPurgePreview(data);
      if (!data.matched) toast.info("No test/probe leads found — you're clean.");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setPurging(false); }
  };

  const confirmPurgeTest = async () => {
    setPurging(true);
    try {
      const { data } = await api.post("/leads/purge-test", { dry_run: false });
      toast.success(`Purged ${data.deleted} test lead${data.deleted === 1 ? "" : "s"}`);
      setPurgePreview(null);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setPurging(false); }
  };

  const filteredLeads = useMemo(() => {
    let out = leads;
    if (sinceFilter && SINCE_MS[sinceFilter]) {
      const cutoff = Date.now() - SINCE_MS[sinceFilter];
      out = out.filter((l) => {
        const t = l.created_at ? new Date(l.created_at).getTime() : 0;
        return t >= cutoff;
      });
    }
    if (ownerFilter === "unassigned") {
      out = out.filter((l) => !l.owner_id);
    } else if (ownerFilter) {
      out = out.filter((l) => l.owner_id === ownerFilter);
    }
    const q = (sourceQuery || "").trim().toLowerCase();
    if (q) {
      out = out.filter((l) => {
        const hay = `${l.name || ""} ${l.email || ""} ${l.city || ""} ${l.industry || ""} ${l.notes || ""} ${l.owner_name || ""}`.toLowerCase();
        return hay.includes(q);
      });
    }
    return out;
  }, [leads, sinceFilter, sourceQuery, ownerFilter]);

  const visibleColumns = statusFilter
    ? COLUMNS.filter((c) => c.key === statusFilter)
    : COLUMNS;

  const clearFilters = () => { setSearchParams({}); setSourceQuery(""); };
  const hasFilter = statusFilter || sinceFilter || sourceQuery || ownerFilter;
  const ownerFilterLabel = ownerFilter === "unassigned"
    ? "Unassigned"
    : (users.find((u) => u.id === ownerFilter)?.name || users.find((u) => u.id === ownerFilter)?.email || "");

  const moveTo = async (leadId, status) => {
    setLeads((ls) =>
      ls.map((l) => (l.id === leadId ? { ...l, status } : l))
    );
    try {
      await api.patch(`/leads/${leadId}`, { status });
    } catch (e) {
      toast.error(formatApiError(e));
      load();
    }
  };

  const toggleSelected = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const exitSelectMode = () => {
    setSelectMode(false);
    setSelected(new Set());
  };

  const selectAllInView = () => {
    setSelected(new Set(filteredLeads.map((l) => l.id)));
  };

  const bulkDelete = async () => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    if (!window.confirm(`Delete ${ids.length} lead${ids.length === 1 ? "" : "s"}? This can't be undone.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/leads/bulk-delete", { ids });
      toast.success(`Deleted ${data.deleted} lead${data.deleted === 1 ? "" : "s"}.`);
      exitSelectMode();
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const deleteAll = async () => {
    setBusy(true);
    try {
      const { data } = await api.delete("/leads/all");
      toast.success(`Deleted all ${data.deleted} lead${data.deleted === 1 ? "" : "s"}.`);
      setConfirmNuke(false);
      setNukeInput("");
      exitSelectMode();
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="p-10">
      <Toaster position="top-right" richColors />
      <div className="mb-8">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="overline mb-3">Pipeline</div>
            <h1 className="font-display text-4xl font-black tracking-tighter mb-2">
              Deal flow
            </h1>
            <p className="text-neutral-500 max-w-xl">
              Drag cards across columns to update status. Click any card to open
              the lead detail, generate a website, or log outreach.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {selectMode ? (
              <>
                <button
                  onClick={selectAllInView}
                  className="lf-btn-outline text-sm inline-flex items-center gap-2"
                  data-testid="leads-select-all"
                >
                  <CheckSquare className="w-4 h-4" /> Select all ({filteredLeads.length})
                </button>
                <button
                  onClick={bulkDelete}
                  disabled={selected.size === 0 || busy}
                  className="lf-btn-primary bg-rose-600 hover:bg-rose-700 text-sm inline-flex items-center gap-2 disabled:opacity-50"
                  data-testid="leads-bulk-delete"
                >
                  <Trash2 className="w-4 h-4" /> Delete {selected.size || ""}
                </button>
                <button
                  onClick={exitSelectMode}
                  className="lf-btn-outline text-sm"
                  data-testid="leads-select-cancel"
                >
                  Cancel
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => setSelectMode(true)}
                  disabled={leads.length === 0}
                  className="lf-btn-outline text-sm inline-flex items-center gap-2 disabled:opacity-40"
                  data-testid="leads-select-mode"
                >
                  <CheckSquare className="w-4 h-4" /> Select
                </button>
                <button
                  onClick={openPurgeTest}
                  disabled={leads.length === 0 || purging}
                  className="text-sm inline-flex items-center gap-2 border border-amber-300 text-amber-800 hover:bg-amber-50 px-3 py-2 disabled:opacity-40"
                  data-testid="leads-purge-test-open"
                  title="Find and delete accidental test/probe leads"
                >
                  <Sparkles className="w-4 h-4" /> {purging ? "Scanning…" : "Purge test leads"}
                </button>
                <button
                  onClick={() => setConfirmNuke(true)}
                  disabled={leads.length === 0}
                  className="text-sm inline-flex items-center gap-2 border border-rose-300 text-rose-700 hover:bg-rose-50 px-3 py-2 disabled:opacity-40"
                  data-testid="leads-delete-all-open"
                >
                  <AlertTriangle className="w-4 h-4" /> Delete all
                </button>
                <button
                  onClick={() => setShowManual(true)}
                  className="lf-btn-outline text-sm inline-flex items-center gap-2"
                  data-testid="leads-manual-add-open"
                >
                  <Plus className="w-4 h-4" /> New lead
                </button>
                <button
                  onClick={() => setShowImport(true)}
                  className="lf-btn-primary text-sm inline-flex items-center gap-2"
                  data-testid="leads-import-open"
                >
                  <Upload className="w-4 h-4" /> Import CSV
                </button>
              </>
            )}
          </div>
        </div>
        <div className="mt-5 flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[240px] max-w-md">
            <Search className="w-4 h-4 text-neutral-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              value={sourceQuery}
              onChange={(e) => setSourceQuery(e.target.value)}
              placeholder="Filter by source, city, industry, or name…"
              className="w-full border border-neutral-300 pl-9 pr-8 py-2 text-sm focus:outline-none focus:border-black"
              data-testid="leads-source-filter"
            />
            {sourceQuery && (
              <button
                onClick={() => setSourceQuery("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-900"
                data-testid="leads-source-filter-clear"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          {sourceQuery && (
            <span className="text-xs font-mono text-neutral-500" data-testid="leads-source-filter-count">
              {filteredLeads.length} match{filteredLeads.length === 1 ? "" : "es"}
            </span>
          )}
          <div className="inline-flex items-center gap-1.5 border border-neutral-300 px-2 py-1.5">
            <UserCircle className="w-3.5 h-3.5 text-neutral-500" />
            <select
              value={ownerFilter}
              onChange={(e) => setOwnerFilter(e.target.value)}
              className="text-xs bg-transparent focus:outline-none"
              data-testid="leads-owner-filter"
            >
              <option value="">All owners</option>
              <option value="unassigned">Unassigned</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>{u.name || u.email}</option>
              ))}
            </select>
          </div>
        </div>
        {hasFilter && (
          <div className="mt-4 inline-flex items-center gap-2 border border-amber-300 bg-amber-50 px-3 py-1.5 text-xs" data-testid="leads-filter-pill">
            <span className="font-mono uppercase tracking-wider text-amber-800">Filter:</span>
            {statusFilter && <span className="text-amber-900 font-semibold">status = {statusFilter}</span>}
            {statusFilter && sinceFilter && <span className="text-amber-500">·</span>}
            {sinceFilter && <span className="text-amber-900 font-semibold">last {sinceFilter}</span>}
            {(statusFilter || sinceFilter) && ownerFilter && <span className="text-amber-500">·</span>}
            {ownerFilter && <span className="text-amber-900 font-semibold">owner = {ownerFilterLabel}</span>}
            {(statusFilter || sinceFilter || ownerFilter) && sourceQuery && <span className="text-amber-500">·</span>}
            {sourceQuery && <span className="text-amber-900 font-semibold">"{sourceQuery}"</span>}
            <button onClick={clearFilters} className="text-amber-800 hover:text-amber-950 ml-1" data-testid="leads-filter-clear">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
      </div>

      {loading ? (
        <div className="text-neutral-500">Loading pipeline…</div>
      ) : filteredLeads.length === 0 ? (
        <div className="border border-dashed border-neutral-300 p-16 text-center">
          <div className="overline mb-3">{hasFilter ? "No leads match this filter" : "No leads yet"}</div>
          <p className="text-neutral-500 max-w-md mx-auto mb-6">
            {hasFilter
              ? <>Try <button onClick={clearFilters} className="border-b border-black">clearing filters</button> or check back later.</>
              : <>Head to <Link to="/discover" className="border-b border-black">Discover</Link> to find and import your first leads.</>}
          </p>
        </div>
      ) : (
        <div
          data-testid={LEADS.board}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4"
        >
          {visibleColumns.map((col) => {
            const items = filteredLeads.filter((l) => l.status === col.key);
            return (
              <div
                key={col.key}
                data-testid={LEADS.column(col.key)}
                className="lf-kanban-col p-3"
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => {
                  if (dragId) moveTo(dragId, col.key);
                  setDragId(null);
                }}
              >
                <div className="flex items-center justify-between mb-3 px-1">
                  <span className={`pill pill-${col.key}`}>{col.label}</span>
                  <span className="text-xs font-mono text-neutral-500">
                    {items.length}
                  </span>
                </div>
                <div className="flex flex-col gap-2">
                  {items.map((l) => {
                    const isSelected = selected.has(l.id);
                    if (selectMode) {
                      return (
                        <div
                          key={l.id}
                          data-testid={LEADS.card(l.id)}
                          onClick={() => toggleSelected(l.id)}
                          className={`lf-kanban-card block cursor-pointer border-2 ${isSelected ? "border-rose-500 bg-rose-50" : "border-transparent"}`}
                        >
                          <div className="flex items-start gap-2">
                            <div className="mt-0.5 flex-shrink-0">
                              {isSelected ? <CheckSquare className="w-4 h-4 text-rose-600" /> : <Square className="w-4 h-4 text-neutral-400" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="font-semibold text-sm mb-1 leading-tight">{l.name}</div>
                              <div className="text-xs text-neutral-500 mb-2">{l.industry} · {l.city}</div>
                              <div className="flex items-center justify-between text-xs">
                                <span className="font-mono text-neutral-500">{l.phone}</span>
                                <span className="font-semibold">{l.rating}★</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    }
                    return (
                      <Link
                        key={l.id}
                        to={`/leads/${l.id}`}
                        data-testid={LEADS.card(l.id)}
                        draggable
                        onDragStart={() => setDragId(l.id)}
                        className="lf-kanban-card block no-underline text-inherit"
                      >
                        <div className="font-semibold text-sm mb-1 leading-tight">
                          {l.name}
                        </div>
                        <div className="text-xs text-neutral-500 mb-2">
                          {l.industry} · {l.city}
                        </div>
                        <div className="flex items-center justify-between text-xs">
                          <span className="font-mono text-neutral-500">
                            {l.phone}
                          </span>
                          <span className="font-semibold">
                            {l.rating}★
                          </span>
                        </div>
                        {l.owner_name && (
                          <div className="mt-1.5 text-[10px] text-neutral-500 flex items-center gap-1">
                            <UserCircle className="w-3 h-3" /> {l.owner_name}
                          </div>
                        )}
                      </Link>
                    );
                  })}
                  {items.length === 0 && (
                    <div className="text-xs text-neutral-400 italic py-6 text-center">
                      Drop leads here
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showImport && (
        <LeadsImportModal
          onClose={() => setShowImport(false)}
          onImported={() => load()}
        />
      )}

      {showManual && (
        <ManualAddLeadModal
          onClose={() => setShowManual(false)}
          onAdded={() => load()}
        />
      )}

      {confirmNuke && (
        <div
          className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
          onClick={() => { setConfirmNuke(false); setNukeInput(""); }}
          data-testid="leads-nuke-modal"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white w-full max-w-md border-2 border-rose-600 shadow-2xl"
          >
            <div className="bg-rose-600 text-white px-5 py-3 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              <span className="font-bold">Delete ALL leads?</span>
            </div>
            <div className="p-5 space-y-4">
              <p className="text-sm text-neutral-700">
                This will permanently delete <strong>every lead in your pipeline</strong> ({leads.length} total),
                along with any generated websites and outreach history tied to them. This <strong>cannot be undone.</strong>
              </p>
              <div>
                <label className="text-xs font-mono uppercase tracking-widest text-neutral-500 block mb-1">
                  Type <span className="text-rose-600 font-bold">DELETE</span> to confirm
                </label>
                <input
                  autoFocus
                  value={nukeInput}
                  onChange={(e) => setNukeInput(e.target.value)}
                  placeholder="DELETE"
                  className="w-full border-2 border-neutral-300 focus:border-rose-600 px-3 py-2 text-sm font-mono focus:outline-none"
                  data-testid="leads-nuke-input"
                />
              </div>
              <div className="flex justify-end gap-2 pt-1">
                <button
                  onClick={() => { setConfirmNuke(false); setNukeInput(""); }}
                  className="lf-btn-outline text-sm"
                  data-testid="leads-nuke-cancel"
                >
                  Cancel
                </button>
                <button
                  onClick={deleteAll}
                  disabled={nukeInput !== "DELETE" || busy}
                  className="text-sm px-4 py-2 bg-rose-600 hover:bg-rose-700 text-white font-semibold disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-2"
                  data-testid="leads-nuke-confirm"
                >
                  <Trash2 className="w-4 h-4" /> {busy ? "Deleting…" : `Delete ${leads.length} leads`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {purgePreview && (
        <div
          className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
          onClick={() => setPurgePreview(null)}
          data-testid="leads-purge-modal"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white w-full max-w-2xl border-2 border-amber-500 shadow-2xl max-h-[85vh] flex flex-col"
          >
            <div className="bg-amber-500 text-white px-5 py-3 flex items-center gap-2 flex-shrink-0">
              <Sparkles className="w-5 h-5" />
              <span className="font-bold">Purge test leads</span>
            </div>
            <div className="p-5 space-y-4 overflow-y-auto">
              {purgePreview.matched === 0 ? (
                <p className="text-sm text-neutral-700">No test or probe leads found — your pipeline is clean.</p>
              ) : (
                <>
                  <p className="text-sm text-neutral-700">
                    Found <strong>{purgePreview.matched}</strong> lead{purgePreview.matched === 1 ? "" : "s"} that look like test/probe rows.
                    Review the list below before confirming. Any generated websites and outreach tied to them will also be removed.
                  </p>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-neutral-500">Match criteria</div>
                  <ul className="text-xs text-neutral-600 list-disc ml-4 space-y-0.5">
                    <li>Phone starts with <code>+1555</code> (reserved test range)</li>
                    <li>Email matches <code>webhook-test-*</code>, <code>testlead-probe-*</code>, or <code>*@example.com</code></li>
                    <li>Source is <code>Manual test</code> / <code>Testing API shape</code></li>
                    <li>Name equals "Test Business", "Test Lead", or "Test Contact"</li>
                    <li>Notes contain "Testing API shape"</li>
                  </ul>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-neutral-500">
                    Preview (first {Math.min(50, purgePreview.matched)} of {purgePreview.matched})
                  </div>
                  <div className="border border-neutral-200 max-h-[300px] overflow-y-auto" data-testid="leads-purge-preview-list">
                    <table className="lf-grid text-xs">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Email</th>
                          <th>Phone</th>
                          <th>Source</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(purgePreview.preview || []).map((r) => (
                          <tr key={r.id}>
                            <td className="font-semibold">{r.name || "—"}</td>
                            <td className="text-neutral-600">{r.email || "—"}</td>
                            <td className="font-mono">{r.phone || "—"}</td>
                            <td className="text-neutral-600">{r.source || "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
            <div className="px-5 py-3 border-t border-neutral-200 flex justify-end gap-2 flex-shrink-0">
              <button
                onClick={() => setPurgePreview(null)}
                className="lf-btn-outline text-sm"
                data-testid="leads-purge-cancel"
              >
                Cancel
              </button>
              {purgePreview.matched > 0 && (
                <button
                  onClick={confirmPurgeTest}
                  disabled={purging}
                  className="text-sm px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white font-semibold inline-flex items-center gap-2 disabled:opacity-50"
                  data-testid="leads-purge-confirm"
                >
                  <Trash2 className="w-4 h-4" /> {purging ? "Purging…" : `Purge ${purgePreview.matched} test lead${purgePreview.matched === 1 ? "" : "s"}`}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
